import type { TagAxis } from "@/hooks/useContentTags";

/**
 * URL helpers for tag chips. Every tag click in the site routes to a single
 * Tag landing page (`/topic/<slug>`, `/pillar/<slug>`, `/role/<slug>`, …)
 * which surfaces matching blog posts, news and resources together.
 *
 * We resolve the tag id at the destination from the URL search param
 * `?id=<uuid>` so we don't need to add a slug column to every vocabulary
 * table. The slug in the path is purely for SEO / shareability.
 *
 * IMPORTANT: `AXIS_TO_PATH` is the single source of truth and `PATH_TO_AXIS`
 * is derived from it, so the two are always exact inverses. Every axis must
 * have its own path — never alias two axes onto one path, because the landing
 * page infers the axis from the path and would then query the wrong
 * vocabulary (this previously made all `pillar` chips resolve to nothing).
 */

/** Path segment for each content_tags axis. Must be unique per axis. */
export const AXIS_TO_PATH = {
  topic: "topic",
  pillar: "pillar",
  role: "role",
  industry: "industry",
  content_type: "format",
  visitor_type: "audience",
} as const satisfies Record<TagAxis, string>;

export type TagLandingAxis = (typeof AXIS_TO_PATH)[TagAxis];

export function slugify(label: string): string {
  return (label || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80) || "tag";
}

export function tagLandingHref(axis: TagAxis, id: string, label: string): string {
  const path = AXIS_TO_PATH[axis];
  if (!path) return "/resources";
  return `/${path}/${slugify(label)}?id=${encodeURIComponent(id)}`;
}

/** Reverse map from a URL path segment back to the canonical content_tags axis. */
export const PATH_TO_AXIS = Object.fromEntries(
  (Object.entries(AXIS_TO_PATH) as [TagAxis, TagLandingAxis][]).map(([axis, path]) => [path, axis]),
) as Record<TagLandingAxis, TagAxis>;

/** Every public tag landing path — used to register routes. */
export const TAG_LANDING_PATHS = Object.values(AXIS_TO_PATH) as TagLandingAxis[];

export const AXIS_LABEL: Record<TagLandingAxis, string> = {
  topic: "Topic",
  pillar: "Solution",
  role: "For",
  industry: "Industry",
  format: "Format",
  audience: "Audience",
};
